import { Injectable, inject } from '@angular/core';
import { CollectionRequestBuilder } from '@web/common';
import { Guid, isNil, Nillable, valueOrDefault } from '@web/utils';
import { catchError, map, Observable, of } from 'rxjs';
import { SelectItem } from '@web/shared/ui';
import {
  OrderNumber,
  PublicOrderApiService,
  PublicOrderShippingMethodCollectionItem,
  PublicOrderShippingMethodCollectionItemResponse,
  ShippingMethodProperty,
  ShippingMethodPropertyMetadata,
} from '@web/shared/module/shop';

@Injectable({
  providedIn: 'root',
})
export class PublicOrderShippingMethodSelectItemsProviderService {
  private readonly orderApi = inject(PublicOrderApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);

  public fetchSelectItems<
    Key extends keyof PublicOrderShippingMethodCollectionItemResponse,
  >(params: {
    orderNumber: OrderNumber;
    shipmentId: Guid;
    key: Key;
    countryId?: Nillable<Guid>;
    query?: Nillable<string>;
  }): Observable<
    SelectItem<PublicOrderShippingMethodCollectionItemResponse[Key]>[]
  > {
    const { orderNumber, shipmentId, key, query, countryId } = params;

    /* prettier-ignore */
    return this.orderApi
      .fetchAvailableShippingMethodCollection({
        request: this.collectionRequestBuilder
          .disablePagination()
          .build(),
        countryId,
        shipmentId,
        orderNumber,
        skipByLoading: true,
        cacheable: false,
      })
      .pipe(
        map((response) =>
          response.items
            .filter((item) => {
              if (isNil(query)) {
                return item;
              }

              return item.name.toLowerCase().includes(query);
            })
            .map((item) => new SelectItem(item.name, item[key])),
        ),
        catchError(() => of([])),
      );
  }

  public fetchShippingMethodById(params: {
    id: Guid;
    countryId?: Nillable<Guid>;
    orderNumber: OrderNumber;
    shipmentId: Guid;
  }): Observable<Nillable<PublicOrderShippingMethodCollectionItem>> {
    const { orderNumber, shipmentId, id, countryId } = params;

    return this.orderApi
      .fetchAvailableShippingMethodCollection({
        request: this.collectionRequestBuilder.disablePagination().build(),
        countryId,
        orderNumber,
        shipmentId,
        skipByLoading: true,
        cacheable: false,
      })
      .pipe(
        map((response) =>
          response.items
            .filter((item) => item.id === id)
            .map(
              (item) =>
                new PublicOrderShippingMethodCollectionItem({
                  code: item.code,
                  name: item.name,
                  id: item.id,
                  description: item.description,
                  shippingPrice: item.shippingPrice,
                  totalPrice: item.totalPrice,
                  properties: item.properties.map(
                    (property) =>
                      new ShippingMethodProperty({
                        code: property.code,
                        name: property.name,
                        value: property.value,
                        metadata: valueOrDefault(property.metadata, []).map(
                          (metadata) =>
                            new ShippingMethodPropertyMetadata({
                              code: metadata.code,
                              value: metadata.value,
                            }),
                        ),
                      }),
                  ),
                }),
            ),
        ),
        map((items) => {
          const [first] = items;

          return first;
        }),
        catchError(() => of(null)),
      );
  }
}
